import { useLayoutEffect, useRef, useState } from 'react';

import type { EditingMode, PageDimensions } from '@aline/common';
import { pxToIn } from '@aline/common';
import type { Editor } from 'slate';
import { Queries } from 'src/editor/queries';
import { useAuthorizedTransforms } from 'src/hooks';

export function roundToOneSixteenthOfAnInch(margin: number): number {
  return Math.round(margin * 16) / 16;
}

export function useDragAndDrop(options: {
  direction: 'horizontal' | 'vertical';
  onDrag(position: number): number;
  onStop(position: number): void;
}): { delta: number | null; start(position: number): void } {
  const { direction, onDrag, onStop } = options;
  const [start, setStart] = useState<number | null>(null);
  const [delta, setDelta] = useState<number | null>(null);

  const onDragRef = useRef(onDrag);
  const onStopRef = useRef(onStop);
  onDragRef.current = onDrag;
  onStopRef.current = onStop;

  useLayoutEffect(() => {
    if (start !== null) {
      const move = (event: MouseEvent): void => {
        event.preventDefault();
        setDelta(
          onDragRef.current((direction === 'horizontal' ? event.clientX : event.clientY) - start)
        );
      };
      const stop = (event: MouseEvent): void => {
        event.preventDefault();
        setStart(null);
      };

      document.body.addEventListener('mouseup', stop);
      document.body.addEventListener('mousemove', move);
      setDelta(0);

      return (): void => {
        document.body.removeEventListener('mouseup', stop);
        document.body.removeEventListener('mousemove', move);
      };
    }
  }, [direction, start]);

  useLayoutEffect(() => {
    if (start === null && delta !== null) {
      onStopRef.current(delta);
      setDelta(null);
    }
  }, [delta, start]);

  return {
    delta: delta !== null ? roundToOneSixteenthOfAnInch(delta) : null,
    start: (position: number): void => setStart(position),
  };
}

function getMargin(
  direction: 'horizontal' | 'vertical',
  edge: 'start' | 'end'
): 'marginLeft' | 'marginRight' | 'marginTop' | 'marginBottom' {
  const margins = {
    horizontal: { end: 'marginRight', start: 'marginLeft' },
    vertical: { end: 'marginBottom', start: 'marginTop' },
  } as const;
  return margins[direction][edge];
}

export function useMarginAdjuster(
  editor: Editor,
  editingMode: EditingMode | null,
  authorId: UUID | null,
  direction: 'horizontal' | 'vertical',
  edge: 'start' | 'end'
): { delta: number | null; start(position: number): void } {
  const side = getMargin(direction, edge);
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  return useDragAndDrop({
    direction,
    onDrag: delta => {
      const pageDimensions = Queries.activePageDimensions(editor, 'in');
      const deltaInIn = pxToIn(edge === 'start' ? delta : delta * -1);
      const indentationProperties = Queries.indentationProperties(editor);
      const newMargin = roundToOneSixteenthOfAnInch(pageDimensions[side] + deltaInIn);
      const oppositeMargin =
        pageDimensions[getMargin(direction, edge === 'start' ? 'end' : 'start')];
      const pageSize = direction === 'horizontal' ? pageDimensions.width : pageDimensions.height;
      const zeroMarginDelta = pageDimensions[side] * -1;
      const indentationAdjustment =
        Math.max(0, indentationProperties.left) +
        Math.max(0, indentationProperties.right) +
        Math.max(0, indentationProperties.firstLine);
      const isNewMarginShrinkingContentSizeBelowAnInch =
        pageSize - indentationAdjustment - newMargin - oppositeMargin < 1;

      if (isNewMarginShrinkingContentSizeBelowAnInch) {
        return roundToOneSixteenthOfAnInch(
          pageSize - indentationAdjustment - oppositeMargin - 1 - pageDimensions[side]
        );
      }

      return Math.max(zeroMarginDelta, deltaInIn);
    },
    onStop: delta => {
      if (!authorId || !editingMode) return;

      const pageDimensions = Queries.activePageDimensions(editor, 'in');
      const updatedPageDimensions: Partial<PageDimensions> = {
        [side]: roundToOneSixteenthOfAnInch(pageDimensions[side] + delta),
      };

      AuthorizedTransforms.updatePageDimensions(editor, {
        authorId,
        editingMode,
        newPageDimensions: updatedPageDimensions,
      });
    },
  });
}
